import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Clock, Calendar, Users, CheckCircle, ExternalLink, Send, ArrowLeft, ChevronDown, ChevronUp } from '../icons';
import { useAuth } from '../context/AuthContext';
import { getMyInquiries, getInquiryMessages, sendInquiryMessage } from '../services/api';
import { joinInquiry, leaveInquiry, onNewMessage } from '../services/socket';
import { useLanguage } from '../context/LanguageContext';
import mediaUrl from '../utils/mediaUrl';

function MyInquiries() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, replied, new, booked
  const [expandedId, setExpandedId] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [replySending, setReplySending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    loadInquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  const loadInquiries = async () => {
    try {
      const res = await getMyInquiries();
      setInquiries(res.data);
      setLoadError(false);
    } catch (err) {
      // Shown instead of "No inquiries yet", which a failed load used to look like
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleThread = async (inquiryId) => {
    if (expandedId === inquiryId) {
      setExpandedId(null);
      setThreadMessages([]);
      setReplyText('');
      setReplyFile(null);
      return;
    }
    setExpandedId(inquiryId);
    setThreadLoading(true);
    setReplyText('');
    setReplyFile(null);
    try {
      const res = await getInquiryMessages(inquiryId);
      setThreadMessages(res.data);
    } catch {
      setThreadMessages([]);
    } finally {
      setThreadLoading(false);
    }
  };

  const handleSendReply = async (inquiryId) => {
    if (!replyText.trim()) return;
    setReplySending(true);
    try {
      await sendInquiryMessage(inquiryId, replyText.trim(), replyFile || undefined);
      setReplyText('');
      setReplyFile(null);
      const res = await getInquiryMessages(inquiryId);
      setThreadMessages(res.data);
      await loadInquiries();
    } catch (err) {
      alert('Failed to send: ' + (err.response?.data?.error || err.message));
    } finally { setReplySending(false); }
  };

  // Scroll to bottom of messages when thread loads
  useEffect(() => {
    if (messagesEndRef.current && expandedId) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    }
  }, [threadMessages, expandedId]);

  // Socket.IO: join/leave inquiry room and listen for real-time messages
  useEffect(() => {
    if (!expandedId) return;
    joinInquiry(expandedId);

    const unsub = onNewMessage(({ inquiryId, message }) => {
      if (inquiryId === expandedId) {
        setThreadMessages(prev => {
          if (prev.find(m => m.id === message.id)) return prev;
          return [...prev, message];
        });
      }
    });

    return () => {
      leaveInquiry(expandedId);
      unsub();
    };
  }, [expandedId]);

  if (authLoading || loading) return <div className="loading-page"><div className="spinner" /></div>;

  const statusColors = { new: '#3b82f6', replied: '#8b5cf6', booked: '#16a34a', unbooked: '#dc2626', read: '#6b7280', declined: '#dc2626' };
  const statusLabels = { new: t('myInquiries.statusAwaitingReply'), replied: t('myInquiries.statusVendorReplied'), booked: t('myInquiries.statusBooked'), unbooked: t('myInquiries.statusNotBooked'), read: t('myInquiries.statusRead'), declined: t('myInquiries.statusDeclined') };

  const filtered = filter === 'all' ? inquiries : inquiries.filter(i => {
    if (filter === 'replied') return i.vendor_reply;
    if (filter === 'unreplied') return !i.vendor_reply;
    return i.status === filter;
  });

  const repliedCount = inquiries.filter(i => i.vendor_reply).length;
  const bookedCount = inquiries.filter(i => i.status === 'booked').length;

  return (
    <div className="my-inquiries-page">
      <div className="container">
        <div style={{ marginBottom: '24px' }}>
          <Link to="/dashboard" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#6b7280', fontSize: '14px', textDecoration: 'none', marginBottom: '12px' }}>
            <ArrowLeft size={16} /> {t('myInquiries.backToDashboard')}
          </Link>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#1f2937', margin: '0 0 4px' }}>
            <MessageSquare size={28} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
            {t('myInquiries.title')}
          </h1>
          <p style={{ color: '#6b7280', margin: 0 }}>
            {t('myInquiries.subtitle')}
          </p>
        </div>

        {/* Summary Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
          <div className="detail-card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{inquiries.length}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{t('myInquiries.totalSent')}</div>
          </div>
          <div className="detail-card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#8b5cf6' }}>{repliedCount}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{t('myInquiries.repliesReceived')}</div>
          </div>
          <div className="detail-card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>{bookedCount}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{t('myInquiries.booked')}</div>
          </div>
          <div className="detail-card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>{inquiries.length - repliedCount}</div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>{t('myInquiries.awaitingReply')}</div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: `${t('myInquiries.all')} (${inquiries.length})` },
            { key: 'replied', label: `${t('myInquiries.replied')} (${repliedCount})` },
            { key: 'unreplied', label: `${t('myInquiries.awaiting')} (${inquiries.length - repliedCount})` },
            { key: 'booked', label: `${t('myInquiries.booked')} (${bookedCount})` },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '8px 16px', border: '1px solid',
                borderColor: filter === f.key ? '#DC143C' : '#d1d5db',
                background: filter === f.key ? '#FEF2F2' : '#fff',
                color: filter === f.key ? '#DC143C' : '#374151',
                borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Inquiry List */}
        {loadError ? (
          <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <h3 style={{ color: '#6b7280', fontWeight: 500 }}>{t('coupleTools.loadFailed')}</h3>
            <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => { setLoading(true); loadInquiries(); }}>
              {t('coupleTools.tryAgain')}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="detail-card" style={{ textAlign: 'center', padding: '48px 24px' }}>
            <MessageSquare size={48} style={{ color: '#d1d5db', marginBottom: '12px' }} />
            <h3 style={{ color: '#6b7280', fontWeight: 500 }}>
              {inquiries.length === 0 ? t('myInquiries.noInquiriesYet') : t('myInquiries.noMatching')}
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '14px' }}>
              {inquiries.length === 0
                ? t('myInquiries.noInquiriesDesc')
                : t('myInquiries.noMatchingDesc')}
            </p>
            {inquiries.length === 0 && (
              <Link to="/vendors" className="btn btn-primary" style={{ marginTop: '12px' }}>
                {t('myInquiries.browseVendors')}
              </Link>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filtered.map((inq) => (
              <div key={inq.id} className="detail-card" style={{ padding: '20px' }}>
                {/* Vendor Info Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', flexShrink: 0,
                      background: inq.vendor_photo ? `url(${mediaUrl(inq.vendor_photo)}) center/cover` : '#f3f4f6',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {!inq.vendor_photo && <MessageSquare size={20} style={{ color: '#9ca3af' }} />}
                    </div>
                    <div>
                      <Link
                        to={`/vendor/${inq.vendor_slug}`}
                        style={{ fontSize: '16px', fontWeight: 600, color: '#1f2937', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {inq.business_name}
                        {inq.vendor_verified && <CheckCircle size={14} style={{ color: '#16a34a' }} />}
                        <ExternalLink size={12} style={{ color: '#9ca3af' }} />
                      </Link>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>
                        {inq.vendor_category} &middot; {inq.vendor_city}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {!inq.couple_read && (
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#DC143C', display: 'inline-block' }} />
                    )}
                    <span style={{
                      fontSize: '12px', fontWeight: 600, padding: '4px 12px', borderRadius: '12px',
                      background: (statusColors[inq.status] || '#6b7280') + '18',
                      color: statusColors[inq.status] || '#6b7280',
                    }}>
                      {statusLabels[inq.status] || inq.status}
                    </span>
                  </div>
                </div>

                {/* Preview (last message or initial) */}
                <div style={{ background: '#f9fafb', padding: '10px 14px', borderRadius: '8px', marginBottom: '12px', border: '1px solid #e5e7eb' }}>
                  <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {inq.vendor_reply || inq.message}
                  </p>
                </div>

                {/* Details Row */}
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '13px', color: '#6b7280' }}>
                  {inq.wedding_date && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Calendar size={14} /> {new Date(inq.wedding_date).toLocaleDateString()}
                    </span>
                  )}
                  {inq.guest_count && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Users size={14} /> {inq.guest_count} {t('myInquiries.guests')}
                    </span>
                  )}
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={14} /> {t('myInquiries.sentPrefix')} {new Date(inq.created_at).toLocaleDateString()}
                  </span>
                  {inq.message_count > 0 && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <MessageSquare size={14} /> {inq.message_count} {t('myInquiries.messages')}
                    </span>
                  )}
                </div>

                {/* View Conversation / Collapse toggle */}
                <button
                  onClick={() => toggleThread(inq.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: '1px solid #d1d5db',
                    borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    color: expandedId === inq.id ? '#DC143C' : '#374151',
                    borderColor: expandedId === inq.id ? '#DC143C' : '#d1d5db',
                    marginBottom: expandedId === inq.id ? '12px' : 0,
                  }}
                >
                  <MessageSquare size={14} />
                  {expandedId === inq.id ? t('myInquiries.hideConversation') : t('myInquiries.viewConversation')}
                  {expandedId === inq.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {/* Expanded Thread */}
                {expandedId === inq.id && (
                  <div>
                    {/* Messages */}
                    <div style={{ background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '14px', maxHeight: '350px', overflowY: 'auto', marginBottom: '12px' }}>
                      {threadLoading ? (
                        <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner-sm" style={{ margin: '0 auto' }} /></div>
                      ) : threadMessages.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>{t('myInquiries.noMessagesYet')}</p>
                      ) : (
                        threadMessages.map((msg) => (
                          <div key={msg.id} style={{
                            marginBottom: '10px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: msg.sender_type === 'couple' ? 'flex-start' : 'flex-end',
                          }}>
                            <div style={{
                              maxWidth: '85%',
                              background: msg.sender_type === 'couple' ? '#fff' : '#EFF6FF',
                              border: `1px solid ${msg.sender_type === 'couple' ? '#e5e7eb' : '#BFDBFE'}`,
                              borderRadius: '10px',
                              padding: '10px 12px',
                            }}>
                              <div style={{ fontSize: '11px', fontWeight: 600, color: msg.sender_type === 'couple' ? '#4f46e5' : '#059669', marginBottom: '3px' }}>
                                {msg.sender_type === 'couple' ? t('myInquiries.you') : inq.business_name}
                              </div>
                              <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.6, color: '#374151', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                              {msg.attachment_url && (
                                <a href={mediaUrl(msg.attachment_url)} target="_blank" rel="noopener noreferrer"
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '12px', color: '#1D4ED8', textDecoration: 'none' }}>
                                  📎 {msg.attachment_name || 'Attachment'}
                                </a>
                              )}
                              <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '3px' }}>{new Date(msg.created_at).toLocaleString()}</div>
                            </div>
                          </div>
                        ))
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Reply box */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <textarea
                        rows={2}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={t('myInquiries.typeMessage')}
                        style={{ padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6b7280', cursor: 'pointer', padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#f9fafb' }}>
                          📎 {replyFile ? replyFile.name : t('myInquiries.attachFile')}
                          <input type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" style={{ display: 'none' }}
                            onChange={(e) => setReplyFile(e.target.files[0] || null)} />
                        </label>
                        {replyFile && <button onClick={() => setReplyFile(null)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>✕</button>}
                        <div style={{ flex: 1 }} />
                        <button
                          className="btn btn-primary"
                          onClick={() => handleSendReply(inq.id)}
                          disabled={replySending || !replyText.trim()}
                          style={{ fontSize: '13px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <Send size={14} /> {replySending ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </div>

                    {/* View vendor profile link */}
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                      <Link
                        to={`/vendor/${inq.vendor_slug}`}
                        style={{ fontSize: '13px', color: '#DC143C', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}
                      >
                        {t('myInquiries.viewVendorProfile')} <ExternalLink size={12} />
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyInquiries;
